/* import __COLOCATED_TEMPLATE__ from './phone-settings.hbs'; */
/* RESPONSIBLE TEAM: team-phone */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type FinVoiceConfigService from 'embercom/services/fin-voice-config-service';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';
import { action } from '@ember/object';
import type { FinVoiceVoice } from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-options';
import type { FinVoiceNumberConfigurationHandoverConfiguration } from 'embercom/models/fin-voice/number-configuration';

interface Args {
  finSetupRuleset: $TSFixMe;
  phoneNumber?: string;
  numberConfiguration?: FinVoiceNumberConfiguration;
}

interface Signature {
  Element: HTMLElement;
  Args: Args;
}

export default class PhoneSettings extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare finVoiceConfigService: FinVoiceConfigService;

  @tracked openSectionId = '';

  get numberConfiguration() {
    return this.args.numberConfiguration;
  }

  @action
  onOpenHandoverSection() {
    this.openSectionId = 'phone-handover';
  }

  @action
  async updateFinBehavior(
    voice: FinVoiceVoice,
    greeting: string,
    greetingOutOfOfficeHours: string,
  ) {
    if (!this.args.phoneNumber) {
      return;
    }

    try {
      await this.finVoiceConfigService.updateVoiceBehavior(
        this.args.phoneNumber,
        voice,
        greeting,
        greetingOutOfOfficeHours,
      );
      this.notificationsService.notifyConfirmation(
        this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.success'),
      );
    } catch (error) {
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.failure'),
      });
    }
  }

  @action
  async updateFinIssueCategorization(attributes?: string[]) {
    if (!this.args.phoneNumber) {
      return;
    }

    try {
      await this.finVoiceConfigService.updateFinIssueCategorization(
        this.args.phoneNumber,
        attributes,
      );
      this.notificationsService.notifyConfirmation(
        this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.success'),
      );
    } catch (error) {
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.failure'),
      });
    }
  }
  @action
  async updateAfterPhoneCallEnds(autoCloseWhenNotEscalated: boolean, defaultTeamId?: string) {
    if (!this.args.phoneNumber) {
      return;
    }

    try {
      await this.finVoiceConfigService.updateAfterPhoneCallEnds(
        this.args.phoneNumber,
        autoCloseWhenNotEscalated,
        defaultTeamId,
      );
      this.notificationsService.notifyConfirmation(
        this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.success'),
      );
    } catch (error) {
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.failure'),
      });
    }
  }

  @action
  async updateHandover(handoverConfig: FinVoiceNumberConfigurationHandoverConfiguration) {
    if (!this.args.phoneNumber) {
      return;
    }

    try {
      await this.finVoiceConfigService.updateHandoverConfiguration(
        this.args.phoneNumber,
        handoverConfig,
      );
      this.notificationsService.notifyConfirmation(
        this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.success'),
      );
    } catch (error) {
      console.error('Error updating handover configuration', error);
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.failure'),
      });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::PhoneSettings': typeof PhoneSettings;
  }
}
